import { useTranslation } from "react-i18next";
import { Icon, cn } from "@nexus/ui";
import type { ParsedInvalidEntry, UrlInvalidReason } from "../../lib/parse-urls";

export interface UrlInputFeedbackProps {
  invalid: ParsedInvalidEntry[];
  unrecognized: string[];
  duplicates: number;
  capped: number;
  className?: string;
}

function reasonKey(reason: UrlInvalidReason): string {
  return `preflight.inputCard.urlInvalidReason.${reason}`;
}

export function UrlInputFeedback({
  invalid,
  unrecognized,
  duplicates,
  capped,
  className,
}: UrlInputFeedbackProps) {
  const { t } = useTranslation();
  const hasErrors = invalid.length > 0 || unrecognized.length > 0;
  const hasNotices = duplicates > 0 || capped > 0;

  if (!hasErrors && !hasNotices) return null;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {hasErrors ? (
        <div
          className="border-error/20 bg-error-subtle/35 flex flex-col gap-2 rounded-md border px-3 py-2.5"
          role="alert"
          aria-live="polite"
        >
          <p className="text-error-subtle-foreground text-xs font-medium">
            {t("preflight.inputCard.urlFixHeading")}
          </p>

          {invalid.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5" aria-label={t("preflight.inputCard.skippedUrls")}>
              {invalid.map((entry, index) => (
                <li key={`${entry.raw}-${index}`}>
                  <span
                    className="border-error/20 bg-background/80 text-error-subtle-foreground inline-flex max-w-full items-center gap-1 rounded-sm border px-2 py-1 font-mono text-[11px] leading-tight"
                    title={t(reasonKey(entry.reason))}
                  >
                    <Icon name="alert-circle" size="xs" color="inherit" />
                    <span className="truncate">{entry.raw}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          {unrecognized.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5">
              {unrecognized.map((token, index) => (
                <li key={`${token}-${index}`}>
                  <span className="border-warning/25 bg-background/80 text-warning inline-flex max-w-full items-center gap-1 rounded-sm border px-2 py-1 font-mono text-[11px] leading-tight">
                    <Icon name="alert-circle" size="xs" color="inherit" />
                    <span className="truncate">{token}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          <p className="text-muted-foreground text-[11px] leading-relaxed">
            {invalid.length > 0
              ? t("preflight.inputCard.urlFixHintInvalid")
              : t("preflight.inputCard.urlFixHintUnrecognized")}
          </p>
        </div>
      ) : null}

      {hasNotices ? (
        <div className="text-muted-foreground flex flex-col gap-1 text-xs" aria-live="polite">
          {duplicates > 0 ? (
            <p className="flex items-center gap-1.5">
              <Icon name="info" size="xs" />
              <span>{t("preflight.inputCard.urlDuplicates", { count: duplicates })}</span>
            </p>
          ) : null}
          {capped > 0 ? (
            <p className="flex items-center gap-1.5">
              <Icon name="info" size="xs" />
              <span>{t("preflight.inputCard.urlCapSkipped", { count: capped })}</span>
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
